import { useEffect, useState } from 'react';
import { createSocket } from '@/utils/socket-init';
import type { GameRole } from '@/types/game';

interface UseGameStateProps {
  role: GameRole;
  teamId?: string;
  sessionId: string;
  userId?: string;
  userName?: string;
}

interface DraftIdea {
  id: string;
  userId: string;
  userName: string;
  content: string;
  timestamp: number;
}

interface Team {
  id: string;
  level: number;
  members: Array<{
    id: string;
    name: string;
  }>;
  draftIdeas: DraftIdea[];
  submittedIdeas?: string[];
}

interface Challenge {
  id: string;
  trigger: string;
  category: string;
  teamId: string;
}

interface GameBoard {
  teamChallenges: Record<string, Challenge>;
  submissionQueue: Array<{
    teamId: string;
    content: string[];
    validated?: boolean;
    timestamp: number;
  }>;
  isFeminineMode?: boolean;
}

interface GameState {
  sessionId: string;
  teams: Record<string, Team>;
  gameBoard: GameBoard;
}

interface SessionInfo {
  sessionId: string;
  masterCount: number;
  teams: Array<{
    teamId: string;
    memberCount: number;
  }>;
}

export interface UseGameStateReturn {
  state: GameState | null;
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  actions: {
    rollDice: (teamId: string, language?: string) => void;
    submitIdeas: (content: string[]) => void;
    validateSubmission: (teamId: string, isValid: boolean) => void;
    updateDraftIdea: (content: string) => void;
    deleteDraftIdea: (ideaId: string) => void;
  };
}

const initialGameState: GameState = {
  sessionId: '',
  teams: {},
  gameBoard: {
    teamChallenges: {},
    submissionQueue: []
  }
};

export function useGameState({
  role,
  teamId,
  sessionId,
  userId,
  userName
}: UseGameStateProps): UseGameStateReturn {
  const [socket, setSocket] = useState<any>(null);
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting');

  useEffect(() => {
    if (!sessionId || !role || !userName) {
      return;
    }

    // Utiliser notre fonction createSocket
    const newSocket = createSocket({
      game: 'storm',
      role,
      sessionId,
      teamId,
      userId,
      userName
    });

    // Gérer la connexion
    newSocket.on('connect', () => {
      console.log('Connected to game server');
      setStatus('connected');
      setGameState(prev => ({
        ...prev,
        sessionId
      }));
    });

    // Gérer la déconnexion
    newSocket.on('disconnect', () => {
      console.log('Disconnected from game server');
      setStatus('disconnected');
    });

    // Gérer les erreurs
    newSocket.on('connect_error', (error: Error) => {
      console.error('Connection error:', error);
      setStatus('error');
    });

    // Écouter les mises à jour d'état
    newSocket.on('gameStateUpdated', (newState: Partial<GameState>) => {
      console.log('Game state updated:', newState);
      setGameState(prev => {
        // Always take the complete state from server
        return {
          ...prev,
          ...newState,
          gameBoard: {
            ...prev.gameBoard,
            ...newState.gameBoard
          }
        };
      });
    });

    // Écouter les mises à jour de session
    newSocket.on('sessionUpdated', (sessionInfo: SessionInfo) => {
      console.log('Session updated:', sessionInfo);
      // Mettre à jour SEULEMENT les métadonnées des équipes, PAS les draftIdeas
      setGameState(prev => {
        const updatedTeams = { ...prev.teams };
        
        // Mettre à jour les métadonnées des équipes existantes
        sessionInfo.teams.forEach(({ teamId, memberCount }) => {
          if (updatedTeams[teamId]) {
            // Préserver les draftIdeas existantes
            updatedTeams[teamId] = {
              ...updatedTeams[teamId],
              id: teamId,
              level: updatedTeams[teamId].level || 1,
              // Ne pas toucher aux draftIdeas et submittedIdeas
            };
          } else {
            // Créer une nouvelle équipe seulement si elle n'existe pas
            updatedTeams[teamId] = {
              id: teamId,
              level: 1,
              members: [],
              draftIdeas: [],
              submittedIdeas: []
            };
          }
        });

        return {
          ...prev,
          teams: updatedTeams
        };
      });
    });

    // Add handlers for real-time idea updates
    newSocket.on('draftIdeaUpdated', ({ teamId: updatedTeamId, idea }: { teamId: string, idea: DraftIdea }) => {
      setGameState(prev => {
        if (!prev) return prev;
        const team = prev.teams[updatedTeamId];
        if (!team) return prev;

        const existingIdeaIndex = team.draftIdeas.findIndex(i => i.id === idea.id);
        const updatedDraftIdeas = existingIdeaIndex >= 0
          ? [
              ...team.draftIdeas.slice(0, existingIdeaIndex),
              idea,
              ...team.draftIdeas.slice(existingIdeaIndex + 1)
            ]
          : [...team.draftIdeas, idea];

        return {
          ...prev,
          teams: {
            ...prev.teams,
            [updatedTeamId]: {
              ...team,
              draftIdeas: updatedDraftIdeas
            }
          }
        };
      });
    });

    newSocket.on('draftIdeaDeleted', ({ teamId: updatedTeamId, ideaId }: { teamId: string, ideaId: string }) => {
      setGameState(prev => {
        if (!prev) return prev;
        const team = prev.teams[updatedTeamId];
        if (!team) return prev;

        return {
          ...prev,
          teams: {
            ...prev.teams,
            [updatedTeamId]: {
              ...team,
              draftIdeas: team.draftIdeas.filter(idea => idea.id !== ideaId)
            }
          }
        };
      });
    });

    // Add handler for submission validation
    newSocket.on('submissionValidated', ({ teamId: updatedTeamId, isValid }: { teamId: string, isValid: boolean }) => {
      setGameState(prev => {
        if (!prev) return prev;

        const team = prev.teams[updatedTeamId];
        if (!team) return prev;

        // Find the current submission in the queue
        const submission = prev.gameBoard.submissionQueue.find(sub => sub.teamId === updatedTeamId);
        if (!submission) return prev;

        if (isValid) {
          // Create a new state object with deep cloning of relevant parts
          const updatedTeams = { ...prev.teams };
          const updatedGameBoard = { 
            teamChallenges: { ...prev.gameBoard.teamChallenges },
            submissionQueue: [...prev.gameBoard.submissionQueue]
          };

          // 1 & 2. Clear team's draft ideas and submitted ideas
          updatedTeams[updatedTeamId] = {
            ...team,
            draftIdeas: [],
            submittedIdeas: undefined
          };

          // 3. Remove team's challenge
          delete updatedGameBoard.teamChallenges[updatedTeamId];

          // 4. Remove from submission queue
          updatedGameBoard.submissionQueue = updatedGameBoard.submissionQueue
            .filter(sub => sub.teamId !== updatedTeamId);

          return {
            ...prev,
            teams: updatedTeams,
            gameBoard: updatedGameBoard
          };
        } else {
          // When rejected:
          // 1. Keep the challenge active
          // 2. Convert submitted ideas back to draft ideas for editing
          // 3. Remove from submission queue
          const rejectedIdeas = submission.content.map(content => ({
            id: `${userId}-${Date.now()}-${Math.random()}`,
            userId: userId || '',
            userName: userName || '',
            content,
            timestamp: Date.now()
          }));

          return {
            ...prev,
            teams: {
              ...prev.teams,
              [updatedTeamId]: {
                ...team,
                draftIdeas: rejectedIdeas,
                submittedIdeas: undefined
              }
            },
            gameBoard: {
              ...prev.gameBoard,
              submissionQueue: prev.gameBoard.submissionQueue.filter(sub => sub.teamId !== updatedTeamId)
            }
          };
        }
      });
    });

    setSocket(newSocket);

    // Cleanup à la déconnexion
    return () => {
      newSocket.disconnect();
    };
  }, [role, teamId, sessionId, userId, userName]);

  const actions = {
    rollDice: (teamId: string, language: string = 'fr') => {
      socket?.emit('rollDice', { teamId, language });
    },

    submitIdeas: (content: string[]) => {
      if (teamId) {
        // Let server handle state updates
        socket?.emit('submitIdeas', {
          teamId,
          content,
          timestamp: Date.now()
        });
      }
    },

    validateSubmission: (teamId: string, isValid: boolean) => {
      socket?.emit('validateSubmission', {
        teamId,
        isValid
      });
    },

    updateDraftIdea: (content: string) => {
      if (teamId && userId && userName) {
        const idea = {
          id: `${userId}-${Date.now()}`,
          userId,
          userName,
          content,
          timestamp: Date.now()
        };
        socket?.emit('updateDraftIdea', {
          teamId,
          idea
        });
      }
    },

    deleteDraftIdea: (ideaId: string) => {
      if (teamId) {
        socket?.emit('deleteDraftIdea', {
          teamId,
          ideaId
        });
      }
    },

    updateFeminineMode: (isFeminineMode: boolean) => {
      socket?.emit('updateFeminineMode', {
        isFeminineMode
      });
    }
  };

  return {
    state: gameState,
    status,
    actions
  };
}
